<template>
    <div class="map-container">
        <div v-if=hasTitle class="map-title">
            <slot name="mapTitle" />
        </div>
        <div ref="mapWrapperEl" class="map-wrapper" />
    </div>
</template>

<script lang="ts" setup>
import {
    computed, onMounted, ref, shallowRef, watch, useSlots,
} from 'vue';
import { LcpMap, IMarker } from '@lcp/lcp-map';

const props = defineProps<{
    mapUrl: string;
    geojsonFeatureId: string;
    activeAreas?: string[];
    markerArray?: Array<IMarker>;
    markerColour?: [number, number, number, number];
    disabledAreas?: string[];
    mapData?: Record<string, number>;
    extentColours?: Array<[number, number, number, number]>;
    extentRange?: Array<number>;
    allowAreaSelect?: boolean;
    allowMapTooltip?: boolean;
    allowLabel?: boolean;
    allowMarkerTooltip?: boolean;
    labelFormatter?:(area: string, value?: number) => string;
    tooltipFormatter?: (area: string, value?: number) => string;
}>();

const map = shallowRef<LcpMap>();
const mapWrapperEl = ref<HTMLDivElement>();
const mapInitialised = ref<boolean>(false);
const slots = useSlots();

const markerIds = ref<Array<string>>([]);

const emit = defineEmits<{
    'update:selectedCountry': [string];
}>();

const hasTitle = computed(() => !!slots.mapTitle);

const mapLabel = computed(() => {
    if (props.activeAreas?.length === 1) {
        const selectedCountry = props.activeAreas[0];
        return props.labelFormatter ? props.labelFormatter(selectedCountry, props.mapData ? props.mapData[selectedCountry] : 0) : '';
    }
    return '';
});

function focusOnAreas (areas: string[]): void {
    const layer = map.value?.getLayer('myMap');

    areas.forEach((area) => {
        layer?.color.setAreaOpacity(area, 255, 1);
    });

    map.value?.focusCameraOnAreas(areas, {
        scale: 0.9,
        maxScale: 4,
        minScale: 0.5,
        cameraAngle: [30, 0, 0],
    });
}

function areaSelected (area: string): void {
    emit('update:selectedCountry', area);
}

async function replaceMarkers (newMarkers: IMarker[]): Promise<void> {
    const markerLayer = map.value?.getOverlayLayer('markers');
    markerLayer?.removeMarkers(markerIds.value);
    markerIds.value = [];
    await markerLayer?.addMarkers(newMarkers);
    newMarkers.forEach(marker => markerIds.value.push(marker.id));
    const markerColour = props.markerColour ?? [20, 30, 52, 255];
    markerLayer?.color.setAllAreas(markerColour);
    markerLayer?.position.setAllZ(50);
    markerLayer?.resumeUpdates();
}

function formatTooltip (featureId: string): string {
    if (props.mapData && props.tooltipFormatter) {
        return props.tooltipFormatter ? props.tooltipFormatter(featureId, props.mapData[featureId]) : '';
    }
    return featureId;
}

onMounted(async () => {
    mapInitialised.value = false;
    map.value = await LcpMap.create(mapWrapperEl.value, {
        layerName: 'myMap',
        geojson: {
            url: props.mapUrl,
            geojsonFeatureId: props.geojsonFeatureId,
            depth: 50,
        },
        tooltip: {
            enabled: props.allowMapTooltip || props.allowMarkerTooltip,
            customSetup: true,
        },
        projection: {
            projection: 'uk',
        },
        camera: {
            panSpeed: 0.0015,
            holdModifierToZoom: false,
            zoomSpeed: 0.04,
            rotateEnabled: false,
        },
    });

    if (props.allowAreaSelect) {
        map.value?.events.onClick((featureId) => {
            if (featureId && !props.disabledAreas?.includes(featureId)) areaSelected(featureId);
        });
    }

    if (props.allowMapTooltip || props.allowMarkerTooltip) {
        map.value.events.onHover((featureId, layer) => {
            if (layer.name === 'myMap' && props.allowMapTooltip && !props.disabledAreas?.includes(featureId)) {
                map.value?.tooltip.setHtml(formatTooltip(featureId));
                map.value?.tooltip.show();
            }
            if (layer.name === 'markers' && props.allowMarkerTooltip) {
                map.value?.tooltip.setHtml(formatTooltip(featureId));
                map.value?.tooltip.show();
            }
        });

        map.value.events.onHoverOut(() => {
            map.value?.tooltip.hide();
        });

        map.value.events.onMouseLeave(() => {
            map.value?.tooltip.hide();
        });
    }

    const layer = map.value.getLayer('myMap');

    if (props.disabledAreas) {
        layer?.disableAreas(props.disabledAreas);
        layer?.color.setAreasTo(props.disabledAreas, [255, 216, 178, 50], 3);
    }

    if (props.activeAreas) {
        layer?.color.setAllAreasOpacity(100, 2);
        layer?.color.setAreasToOpacity(props.activeAreas, 255, 2);
        focusOnAreas(props.activeAreas);
    }

    if (props.mapData && props.extentRange && props.extentColours) {
        layer?.color.setValuesForAreas(props.mapData);
        layer?.color.setRange(props.extentColours);
        layer?.color.setExtents(props.extentRange);
    } else {
        layer?.color.setAllAreas([255, 216, 178, 255]);
    }

    const markerLayer = await map.value?.createOverlayLayer({ layerName: 'markers' });
    markerLayer.disableAreaAnimation();
    if (!props.allowMarkerTooltip) markerLayer.disable();
    if (props.markerArray) {
        await markerLayer.addMarkers(props.markerArray);
        props.markerArray.forEach(marker => markerIds.value.push(marker.id));
        markerLayer.position.setAllZ(50);
        const markerColour = props.markerColour ?? [20, 30, 52, 255];
        markerLayer.color.setAllAreas(markerColour);
    }
    mapInitialised.value = true;
});

watch(() => props.extentRange, (newExtents) => {
    const layer = map.value?.getLayer('myMap');
    if (newExtents) {
        layer?.color.setExtents(newExtents);
    }
});

watch(() => [props.activeAreas, mapLabel.value, mapInitialised.value], () => {
    if (!mapInitialised.value) return;
    if (props.activeAreas) {
        const layer = map.value?.getLayer('myMap');
        layer?.color.setAllAreasOpacity(100, 2);
        layer?.color.setAreasToOpacity(props.activeAreas, 255, 2);
        focusOnAreas(props.activeAreas);

        layer?.html.removeAllElements();
        if (props.allowLabel) { // set label
            if (layer && props.activeAreas.length === 1) {
                const countryId = props.activeAreas[0];
                const labelDiv = document.createElement('div');
                labelDiv.id = 'map-country-label';
                labelDiv.innerHTML = mapLabel.value;
                const [x, y] = layer.position.getArea(countryId);
                layer?.html.addElement(labelDiv, {
                    id: countryId,
                    x,
                    y,
                    z: 1,
                    rotation: [20, 0, 0],
                });
            }
        }
    }
});

watch(() => props.markerArray, async (newVal) => {
    await replaceMarkers(newVal ?? []);
});

watch(() => props.mapData, () => {
    if (!props.mapData) return;
    const layer = map.value?.getLayer('myMap');
    layer?.color.setValuesForAreas(props.mapData);
});

</script>

<style lang="scss" scoped>
.tooltip-grid {
    display: grid;
    grid-template-columns: 60px 1fr;
    gap: var(--defaultSpacing);
}
.map-title {
    z-index: 1;
    text-align: center;
    color: var(--primaryFont);
}
.map-container {
    display: grid;
    height: 100%;
    overflow: hidden;

    .map-wrapper {
        height: 100%;
        overflow: hidden;
        :deep(.lcp-map) {
            display: flex;
            width: 100%;
            height: 100%;
            background: var(--neutral100);
            overflow: hidden;
            canvas {
                flex: 1;
                width: 100%;
            }
        }
    }
}
</style>
